import React, { useEffect, useMemo, useRef, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import DirectoryHeader from './DirectoryHeader.jsx';
import { formatDisplayName, statusBadge, terminalLabel } from './directory-ui.js';

const INPUT = {
  width: '100%',
  padding: '10px 14px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 14,
  fontFamily: 'Montserrat, Arial, sans-serif',
  outline: 'none',
};

const MANAGER_PERMS = ['ops_dashboard', 'daily_entry', 'scorecard', 'directory', 'scheduler', 'fleet'];
const ADMIN_PERMS = [...MANAGER_PERMS, 'finance', 'permissions', 'onboarding'];
const ALL_PERM_OPTIONS = ['ops_dashboard', 'daily_entry', 'scorecard', 'directory', 'scheduler', 'fleet', 'finance', 'permissions', 'onboarding'];

interface FieldProps {
  label: string;
  value: unknown;
  editable?: boolean;
  onChange?: (value: string) => void;
  type?: string;
}

interface CheckboxFieldProps {
  label: string;
  value: unknown;
  editable?: boolean;
  onChange?: (value: boolean) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  value: unknown;
  editable?: boolean;
  onChange?: (value: string) => void;
  options: SelectOption[];
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

type EmployeeRecord = Record<string, unknown>;

interface Asset {
  id: string | number;
  asset_type?: string;
  asset_label?: string;
  serial_number?: string;
  [key: string]: unknown;
}

interface EditLogEntry {
  id: string | number;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  changed_by_email?: string;
  created_at?: string;
}

interface DirectoryDetailCardProps {
  employee: EmployeeRecord | null;
  currentUser: { id?: string | number } | null;
  assets: Asset[];
  editLog: EditLogEntry[];
  canEditCompensation: boolean;
  canEditHrSensitive: boolean;
  canManageRoles: boolean;
  saving: boolean;
  reporting: boolean;
  roleSubmitting: boolean;
  onSave: (form: Record<string, unknown>) => Promise<{ ok?: boolean } | undefined>;
  onSaveCompensation: (form: Record<string, unknown>) => Promise<{ ok?: boolean } | undefined>;
  onSaveHrSensitive: (form: Record<string, unknown>) => Promise<{ ok?: boolean } | undefined>;
  onReportAsset: (data: { asset_id: string; status: string; notes: string }) => Promise<{ ok?: boolean } | undefined>;
  onPromote: (data: Record<string, unknown>) => Promise<{ ok?: boolean } | undefined>;
  onDemote: () => Promise<unknown>;
  onUploadPhoto?: (data: string | ArrayBuffer | null) => Promise<unknown>;
  onBack: () => void;
}

function Field({ label, value, editable = false, onChange, type = 'text' }: FieldProps) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {editable ? (
        <input type={type} value={(value as string) || ''} onChange={(event) => onChange?.(event.target.value)} style={INPUT} />
      ) : (
        <div style={{ color: value ? C.text : C.textMuted, fontSize: 14 }}>{String(value) || '—'}</div>
      )}
    </div>
  );
}

function CheckboxField({ label, value, editable = false, onChange }: CheckboxFieldProps) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {editable ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.text }}>
          <input type="checkbox" checked={!!value} onChange={(event) => onChange?.(event.target.checked)} />
          {value ? 'Yes' : 'No'}
        </label>
      ) : (
        <div style={{ color: value ? C.text : C.textMuted, fontSize: 14 }}>{value ? 'Yes' : 'No'}</div>
      )}
    </div>
  );
}

function SelectField({ label, value, editable = false, onChange, options }: SelectFieldProps) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {editable ? (
        <select value={(value as string) || ''} onChange={(event) => onChange?.(event.target.value)} style={INPUT}>
          {options.map((option: SelectOption) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : (
        <div style={{ color: value ? C.text : C.textMuted, fontSize: 14 }}>{String(value) || '—'}</div>
      )}
    </div>
  );
}

function Section({ title, children }: SectionProps) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>{children}</div>
    </div>
  );
}

export default function DirectoryDetailCard({ employee, currentUser, assets, editLog, canEditCompensation, canEditHrSensitive, canManageRoles, saving, reporting, roleSubmitting, onSave, onSaveCompensation, onSaveHrSensitive, onReportAsset, onPromote, onDemote, onUploadPhoto, onBack }: DirectoryDetailCardProps) {
  const [editing, setEditing] = useState(false);
  const [compEditing, setCompEditing] = useState(false);
  const [hrEditing, setHrEditing] = useState(false);
  const [reportingAssetId, setReportingAssetId] = useState('');
  const [reportStatus, setReportStatus] = useState('damaged');
  const [reportNotes, setReportNotes] = useState('');
  const [promoteRole, setPromoteRole] = useState('manager');
  const [promotePwdEmail, setPromotePwdEmail] = useState('');
  const [promotePerms, setPromotePerms] = useState(MANAGER_PERMS);
  const [promoteCode, setPromoteCode] = useState('');
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [compForm, setCompForm] = useState<Record<string, unknown>>({});
  const [hrForm, setHrForm] = useState<Record<string, unknown>>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditing(false);
    setCompEditing(false);
    setHrEditing(false);
    setReportingAssetId('');
    setReportStatus('damaged');
    setReportNotes('');
    setPromoteRole('manager');
    const suggestedEmail = `${(String(employee?.first_name || '')).toLowerCase().replace(/[^a-z]/g, '')}.${(String(employee?.last_name || '')).toLowerCase().replace(/[^a-z]/g, '')}@prestigeworlddrive.com`;
    setPromotePwdEmail(employee ? suggestedEmail : '');
    setPromotePerms(MANAGER_PERMS);
    setPromoteCode('');
    setForm({});
    setCompForm({});
    setHrForm({});
  }, [employee?.id]);

  const displayName = formatDisplayName(employee || {});
  const missingPhoto = !employee?.photo_url;
  const recentEdits = editLog.slice(0, 5) as EditLogEntry[];
  const status = statusBadge(employee?.status as string | undefined);

  const dirty = useMemo(() => Object.keys(form).length > 0, [form]);
  const compDirty = useMemo(() => Object.keys(compForm).length > 0, [compForm]);
  const hrDirty = useMemo(() => Object.keys(hrForm).length > 0, [hrForm]);

  if (!employee) return null;

  const canPromoteTarget = canManageRoles && currentUser?.id !== employee.id && !['manager', 'admin', 'god_mode'].includes(employee.role as string);
  const canDemoteTarget = canManageRoles && currentUser?.id !== employee.id && (employee.role === 'manager' || employee.email === 'adam.hoemberg@prestigeworlddrive.com');

  function currentValue(field: string) {
    return form[field] !== undefined ? form[field] : (employee as EmployeeRecord)[field];
  }

  function currentCompValue(field: string) {
    return compForm[field] !== undefined ? compForm[field] : (employee as EmployeeRecord)[field];
  }

  function currentHrValue(field: string) {
    return hrForm[field] !== undefined ? hrForm[field] : (employee as EmployeeRecord)[field];
  }

  function updateField(field: string, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateCompField(field: string, value: string) {
    setCompForm((current) => ({ ...current, [field]: value }));
  }

  function updateCompCheckbox(field: string, value: boolean) {
    setCompForm((current) => {
      var next = { ...current, [field]: value };
      if (field === 'per_diem_eligible' && value === false) next.per_diem_rate = '';
      return next;
    });
  }

  function updateHrField(field: string, value: string | boolean) {
    setHrForm((current) => ({ ...current, [field]: value }));
  }

  function handlePromoteRoleChange(nextRole: string) {
    setPromoteRole(nextRole);
    if (nextRole === 'admin') setPromotePerms(ADMIN_PERMS);
    else if (nextRole === 'manager') setPromotePerms(MANAGER_PERMS);
    else setPromotePerms([]);
  }

  function togglePromotePerm(permission: string) {
    setPromotePerms((current) => current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission]);
  }

  async function handleSave() {
    const result = await onSave(form);
    if (result?.ok) {
      setEditing(false);
      setForm({});
    }
  }

  async function handleCompSave() {
    const payload = { ...compForm };
    if (currentCompValue('per_diem_eligible') !== true) payload.per_diem_rate = '';
    const result = await onSaveCompensation(payload);
    if (result?.ok) {
      setCompEditing(false);
      setCompForm({});
    }
  }

  async function handleHrSave() {
    const result = await onSaveHrSensitive(hrForm);
    if (result?.ok) {
      setHrEditing(false);
      setHrForm({});
    }
  }

  async function handleReportAsset() {
    const result = await onReportAsset({
      asset_id: reportingAssetId,
      status: reportStatus,
      notes: reportNotes,
    });
    if (result?.ok) {
      setReportingAssetId('');
      setReportStatus('damaged');
      setReportNotes('');
    }
  }

  async function handlePromote() {
    const result = await onPromote({
      action: 'promote',
      new_role: promoteRole,
      permissions: promotePerms,
      pwd_email: promoteRole === 'manager' || promoteRole === 'admin' ? promotePwdEmail : undefined,
      confirmation_code: promoteCode,
    });

    if (result?.ok) {
      setPromoteCode('');
    }
  }

  async function handleDemote() {
    await onDemote();
  }

  function handlePhotoSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !onUploadPhoto) return;
    const reader = new FileReader();
    reader.onload = async function() {
      setUploadingPhoto(true);
      await onUploadPhoto(reader.result);
      setUploadingPhoto(false);
    };
    reader.readAsDataURL(file);
  }

  const actions = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {!editing ? (
        <button type="button" onClick={() => setEditing(true)} style={{ padding: '8px 16px', background: C.gold, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
          Edit
        </button>
      ) : (
        <>
          <button type="button" onClick={() => { setEditing(false); setForm({}); }} style={{ padding: '8px 16px', background: C.panelSoft, color: C.textMuted, border: `1px solid ${C.borderSoft}`, borderRadius: 8, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" disabled={!dirty || saving} onClick={handleSave} style={{ padding: '8px 16px', background: C.green, color: C.navy, border: 'none', borderRadius: 8, cursor: !dirty || saving ? 'not-allowed' : 'pointer', opacity: !dirty || saving ? 0.5 : 1, fontWeight: 700 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DirectoryHeader
        title={displayName}
        subtitle={`ID: ${employee.driver_id || '—'} · ${employee.email || '—'}`}
        onBack={onBack}
        actions={actions}
      />

      <div style={{ ...LAYOUT.subPanel, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div
          style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: missingPhoto ? `3px solid ${C.red}` : `3px solid ${C.gold}`, flexShrink: 0, cursor: onUploadPhoto ? 'pointer' : 'default', position: 'relative' }}
          onClick={() => { if (onUploadPhoto && photoInputRef.current) photoInputRef.current.click(); }}
          title={onUploadPhoto ? 'Click to upload photo' : undefined}
        >
          {employee.photo_url ? (
            <img src={employee.photo_url as string} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 20 }}>
              {(String(employee.first_name || '?'))[0]}
            </div>
          )}
          {uploadingPhoto && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>...</div>
          )}
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />

        <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {missingPhoto ? <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'rgba(255,107,107,0.15)', color: C.red }}>Photo missing</span> : null}
          {employee.status ? <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: status.background, color: status.color }}>{String(employee.status)}</span> : null}
          {employee.staffing_type ? <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'rgba(201,162,39,0.15)', color: C.gold }}>{String(employee.staffing_type)}</span> : null}
          {employee.role ? <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.05)', color: C.text }}>{String(employee.role)}</span> : null}
          {employee.fedex_experience ? <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: 'rgba(100,50,200,0.15)', color: '#b794f6' }}>FedEx</span> : null}
        </div>
      </div>

      <Section title="Identity">
        <Field label="First name" value={currentValue('first_name')} editable={editing} onChange={(value) => updateField('first_name', value)} />
        <Field label="Last name" value={currentValue('last_name')} editable={editing} onChange={(value) => updateField('last_name', value)} />
        <Field label="Nickname" value={currentValue('nickname')} editable={editing} onChange={(value) => updateField('nickname', value)} />
        <Field label="Middle name" value={currentValue('middle_name')} editable={editing} onChange={(value) => updateField('middle_name', value)} />
        <Field label="Driver ID" value={employee.driver_id} />
        <Field label="Phone" value={currentValue('phone')} editable={editing} onChange={(value) => updateField('phone', value)} />
        <Field label="Email" value={currentValue('email')} editable={editing} onChange={(value) => updateField('email', value)} type="email" />
        <Field label="Hire date" value={currentValue('hire_date')} editable={editing} onChange={(value) => updateField('hire_date', value)} type="date" />
      </Section>

      <Section title="Employment">
        <SelectField label="Status" value={currentValue('status')} editable={editing} onChange={(value) => updateField('status', value)} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'terminated', label: 'Terminated' }, { value: 'on_leave', label: 'On Leave' }]} />
        <SelectField label="Staffing type" value={currentValue('staffing_type')} editable={editing} onChange={(value) => updateField('staffing_type', value)} options={[{ value: 'local', label: 'Local' }, { value: 'contingent', label: 'Contingent' }, { value: 'lead', label: 'Lead' }, { value: 'traveling_manager', label: 'Traveling Manager' }, { value: 'admin', label: 'Admin' }, { value: 'local_manager', label: 'Local Manager' }]} />
        <Field label="Role" value={employee.role} />
        <Field label="Terminal" value={terminalLabel(employee)} />
      </Section>

      <Section title="Emergency contact">
        <Field label="Contact name" value={currentValue('emergency_contact_name')} editable={editing} onChange={(value) => updateField('emergency_contact_name', value)} />
        <Field label="Contact phone" value={currentValue('emergency_contact_phone')} editable={editing} onChange={(value) => updateField('emergency_contact_phone', value)} />
        <Field label="Relationship" value={currentValue('emergency_contact_relationship')} editable={editing} onChange={(value) => updateField('emergency_contact_relationship', value)} />
      </Section>

      {canEditCompensation ? (
        <Section title="Compensation">
          <Field label="Pay rate" value={currentCompValue('pay_rate')} editable={compEditing} onChange={(value) => updateCompField('pay_rate', value)} type="number" />
          <SelectField label="Rate type" value={currentCompValue('rate_type')} editable={compEditing} onChange={(value) => updateCompField('rate_type', value)} options={[{ value: 'hourly', label: 'Hourly' }, { value: 'daily', label: 'Daily' }, { value: 'salary', label: 'Salary' }]} />
          <CheckboxField label="Per diem eligible" value={currentCompValue('per_diem_eligible')} editable={compEditing} onChange={(value) => updateCompCheckbox('per_diem_eligible', value)} />
          <Field
            label="Per diem rate"
            value={currentCompValue('per_diem_rate')}
            editable={compEditing && currentCompValue('per_diem_eligible') === true}
            onChange={(value) => updateCompField('per_diem_rate', value)}
            type="number"
          />
          <Field label="Pay note" value={currentCompValue('pay_rate_note')} editable={compEditing} onChange={(value) => updateCompField('pay_rate_note', value)} />
          {currentCompValue('per_diem_eligible') !== true ? (
            <div style={{ gridColumn: '1 / -1', color: C.textMuted, fontSize: 12 }}>
              Per diem rate is inactive while per diem eligibility is off.
            </div>
          ) : null}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!compEditing ? <button type="button" onClick={() => setCompEditing(true)} style={{ padding: '8px 16px', background: 'rgba(201,162,39,0.15)', color: C.gold, border: '1px solid rgba(201,162,39,0.3)', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>Edit compensation</button> : <><button type="button" onClick={() => { setCompEditing(false); setCompForm({}); }} style={{ padding: '8px 16px', background: C.panelSoft, color: C.textMuted, border: `1px solid ${C.borderSoft}`, borderRadius: 8, cursor: 'pointer' }}>Cancel</button><button type="button" disabled={!compDirty || saving} onClick={handleCompSave} style={{ padding: '8px 16px', background: C.gold, color: C.navy, border: 'none', borderRadius: 8, cursor: !compDirty || saving ? 'not-allowed' : 'pointer', opacity: !compDirty || saving ? 0.5 : 1, fontWeight: 700 }}>{saving ? 'Saving...' : 'Save GG42'}</button></>}
            <div style={{ color: C.textMuted, fontSize: 12 }}>Finance-gated slice. Save path sends GG42 confirmation code.</div>
          </div>
        </Section>
      ) : null}

      {canEditHrSensitive ? (
        <Section title="HR sensitive">
          <Field label="Date of birth" value={currentHrValue('dob')} editable={hrEditing} onChange={(value) => updateHrField('dob', value)} type="date" />
          <Field label="Driver license" value={currentHrValue('dl_number')} editable={hrEditing} onChange={(value) => updateHrField('dl_number', value)} />
          <Field label="Address line 1" value={currentHrValue('address_line1')} editable={hrEditing} onChange={(value) => updateHrField('address_line1', value)} />
          <Field label="Address line 2" value={currentHrValue('address_line2')} editable={hrEditing} onChange={(value) => updateHrField('address_line2', value)} />
          <Field label="City" value={currentHrValue('address_city')} editable={hrEditing} onChange={(value) => updateHrField('address_city', value)} />
          <Field label="State" value={currentHrValue('address_state')} editable={hrEditing} onChange={(value) => updateHrField('address_state', value)} />
          <Field label="ZIP" value={currentHrValue('address_zip')} editable={hrEditing} onChange={(value) => updateHrField('address_zip', value)} />
          <Field label="Shirt size" value={currentHrValue('shirt_size')} editable={hrEditing} onChange={(value) => updateHrField('shirt_size', value)} />
          <Field label="Pants size" value={currentHrValue('pants_size')} editable={hrEditing} onChange={(value) => updateHrField('pants_size', value)} />
          <Field label="Jacket size" value={currentHrValue('jacket_size')} editable={hrEditing} onChange={(value) => updateHrField('jacket_size', value)} />
          <Field label="Reports to email" value={currentHrValue('reports_to_email')} editable={hrEditing} onChange={(value) => updateHrField('reports_to_email', value)} type="email" />
          <CheckboxField label="FedEx experience" value={currentHrValue('fedex_experience')} editable={hrEditing} onChange={(value) => updateHrField('fedex_experience', value)} />
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!hrEditing ? <button type="button" onClick={() => setHrEditing(true)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.08)', color: C.text, border: `1px solid ${C.borderSoft}`, borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>Edit HR fields</button> : <><button type="button" onClick={() => { setHrEditing(false); setHrForm({}); }} style={{ padding: '8px 16px', background: C.panelSoft, color: C.textMuted, border: `1px solid ${C.borderSoft}`, borderRadius: 8, cursor: 'pointer' }}>Cancel</button><button type="button" disabled={!hrDirty || saving} onClick={handleHrSave} style={{ padding: '8px 16px', background: C.green, color: C.navy, border: 'none', borderRadius: 8, cursor: !hrDirty || saving ? 'not-allowed' : 'pointer', opacity: !hrDirty || saving ? 0.5 : 1, fontWeight: 700 }}>{saving ? 'Saving...' : 'Save HR'}</button></>}
            <div style={{ color: C.textMuted, fontSize: 12 }}>HR-sensitive slice only. Assets and admin controls remain separate.</div>
          </div>
        </Section>
      ) : null}

      <Section title="Assets overview">
        <Field label="Tracked assets" value={assets.length ? String(assets.length) : '0'} />
        <Field label="iPad IMEI" value={employee.ipad_imei} />
        <Field label="Scanner serial" value={employee.scanner_serial} />
        <Field label="Mudflap card" value={employee.mudflap_card} />
        {assets.length > 0 ? <><SelectField label="Asset to report" value={reportingAssetId} editable={true} onChange={setReportingAssetId} options={[{ value: '', label: 'Select asset' }, ...assets.map((asset: Asset) => ({ value: String(asset.id), label: `${asset.asset_type || 'Asset'} · ${asset.asset_label || asset.serial_number || asset.id}` }))]} /><SelectField label="Report status" value={reportStatus} editable={true} onChange={setReportStatus} options={[{ value: 'damaged', label: 'Damaged' }, { value: 'lost', label: 'Lost' }, { value: 'missing', label: 'Missing' }, { value: 'assigned', label: 'Assigned' }]} /><Field label="Report notes" value={reportNotes} editable={true} onChange={setReportNotes} /><div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><button type="button" disabled={!reportingAssetId || !reportNotes.trim() || reporting} onClick={handleReportAsset} style={{ padding: '8px 16px', background: C.red, color: '#fff', border: 'none', borderRadius: 8, cursor: !reportingAssetId || !reportNotes.trim() || reporting ? 'not-allowed' : 'pointer', opacity: !reportingAssetId || !reportNotes.trim() || reporting ? 0.5 : 1, fontWeight: 700 }}>{reporting ? 'Reporting...' : 'Report asset issue'}</button><div style={{ color: C.textMuted, fontSize: 12 }}>Per-employee asset issue flow only. All-assets dashboard and reassignment stay separate.</div></div></> : null}
      </Section>

      {canManageRoles ? (
        <Section title="Role controls">
          {canPromoteTarget ? (
            <>
              <SelectField label="Promote to" value={promoteRole} editable={true} onChange={handlePromoteRoleChange} options={[{ value: 'lead_driver', label: 'Lead Driver' }, { value: 'manager', label: 'Manager' }, { value: 'admin', label: 'Admin' }]} />
              {(promoteRole === 'manager' || promoteRole === 'admin') ? <Field label="PWD email" value={promotePwdEmail} editable={true} onChange={setPromotePwdEmail} type="email" /> : <div />}
              <Field label="Confirmation code" value={promoteCode} editable={true} onChange={setPromoteCode} />
              <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 8 }}>
                <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Permissions</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  {ALL_PERM_OPTIONS.map((permission) => (
                    <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: 8, color: promotePerms.includes(permission) ? C.green : C.textMuted }}>
                      <input type="checkbox" checked={promotePerms.includes(permission)} onChange={() => togglePromotePerm(permission)} />
                      {permission.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" disabled={promoteCode !== 'pen15' || roleSubmitting} onClick={handlePromote} style={{ padding: '8px 16px', background: 'rgba(78,205,196,0.15)', color: C.green, border: '1px solid rgba(78,205,196,0.3)', borderRadius: 8, cursor: promoteCode !== 'pen15' || roleSubmitting ? 'not-allowed' : 'pointer', opacity: promoteCode !== 'pen15' || roleSubmitting ? 0.5 : 1, fontWeight: 700 }}>{roleSubmitting ? 'Promoting...' : 'Promote'}</button>
                <div style={{ color: C.textMuted, fontSize: 12 }}>God-mode slice only. Uses existing role-change route and pen15 confirmation.</div>
              </div>
            </>
          ) : null}

          {canDemoteTarget ? (
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" disabled={roleSubmitting} onClick={handleDemote} style={{ padding: '8px 16px', background: 'rgba(255,107,107,0.12)', color: C.red, border: '1px solid rgba(255,107,107,0.25)', borderRadius: 8, cursor: roleSubmitting ? 'not-allowed' : 'pointer', opacity: roleSubmitting ? 0.5 : 1, fontWeight: 700 }}>{roleSubmitting ? 'Demoting...' : 'Demote to Lead Driver'}</button>
              <div style={{ color: C.textMuted, fontSize: 12 }}>Demotion remains isolated to the role-change route.</div>
            </div>
          ) : null}

          {!canPromoteTarget && !canDemoteTarget ? <div style={{ color: C.textMuted, fontSize: 13 }}>No role-control actions available for this employee.</div> : null}
        </Section>
      ) : null}

      <div style={LAYOUT.subPanel}><div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>Recent edit history</div>{recentEdits.length === 0 ? <div style={{ color: C.textMuted, fontSize: 13 }}>No edit history returned for this employee.</div> : <div style={{ display: 'grid', gap: 8 }}>{recentEdits.map((entry: EditLogEntry) => (<div key={entry.id} style={{ padding: 10, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}><div style={{ fontSize: 13, color: C.text }}><span style={{ color: C.gold }}>{entry.field_name}</span> · {entry.old_value || '—'} → {entry.new_value || '—'}</div><div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{entry.changed_by_email || 'unknown'} · {entry.created_at || 'unknown time'}</div></div>))}</div>}</div>
    </div>
  );
}
